import type { ComponentPropsWithRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const progressBarColorVariants = cva('', {
    variants: {
        variant: {
            black: 'bg-snk-black',
            pink: 'bg-snk-ndulge',
            green: 'bg-snk-status-success-dark',
            matches: 'bg-snk-matches-pink',
        },
    },
    defaultVariants: {
        variant: 'black',
    },
});

export interface ProgressBarProps
    extends Omit<ComponentPropsWithRef<'div'>, 'children'>,
        VariantProps<typeof progressBarColorVariants> {
    value?: number;
    animationDuration?: string;
}

export const ProgressBar = ({
    value = 0,
    variant,
    className,
    animationDuration = 'duration-300',
    ref,
    ...props
}: ProgressBarProps) => {
    const normalizedValue = Number.isFinite(value) ? value : 0;
    const progress = Math.min(100, Math.max(0, normalizedValue));
    const colorClassName = progressBarColorVariants({ variant });
    const isMatches = variant === 'matches';

    return (
        <div
            ref={ref}
            role="progressbar"
            aria-label="progress-bar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            className={cn('relative flex h-4 w-full items-center', className)}
            {...props}>
            <div
                data-slot="progress-bar-track"
                className={cn(
                    'w-full rounded-full bg-muted',
                    isMatches ? 'h-2 p-0.5 shadow-[inset_0_0_1px_0_rgba(0,0,0,0.25)]' : 'h-0.5'
                )}>
                <div
                    data-slot="progress-bar-indicator"
                    className={cn(
                        'relative h-full rounded-full transition-[width] ease-out',
                        colorClassName,
                        animationDuration
                    )}
                    style={{ width: `${progress}%` }}>
                    {progress > 0 && !isMatches ? (
                        <span
                            aria-hidden="true"
                            data-slot="progress-bar-end-cap"
                            className={cn(
                                'absolute top-1/2 right-0 h-1.5 w-1.5 -translate-y-1/2 rounded-full',
                                colorClassName
                            )}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};
